import React, { useRef, useEffect, useState } from "react";
import "./ScrollingText.css"; // Import external CSS file
import { ScrollingTextProps, Styles } from "./types";

// Improved styling to ensure proper containment
const styles: Styles = {
  scrollingContainer: {
    width: "100%",
    overflow: "hidden",
    position: "relative",
    display: "block",
  },
  scrollingText: {
    whiteSpace: "nowrap",
    display: "inline-block",
    textOverflow: "ellipsis",
  },
  animate: {
    paddingLeft: "100%", // Start from outside the container
  },
};

const ScrollingText: React.FC<ScrollingTextProps> = ({
  text,
  className = "",
  separator = "",
  speed = 30,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const [shouldScroll, setShouldScroll] = useState<boolean>(false);
  const [animationDuration, setAnimationDuration] = useState<number>(speed);

  useEffect(() => {
    const checkOverflow = (): void => {
      if (containerRef.current && textRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        const textWidth = textRef.current.scrollWidth;

        // Consider text overflowing if it's wider than the container
        const isOverflowing = textWidth > containerWidth;
        setShouldScroll(isOverflowing);

        // Adjust animation speed based on container width
        if (isOverflowing) {
          // Use normal speed for mobile views (320px) and maintain proportional speed for other sizes
          const adaptiveSpeed = containerWidth < 320 ? 10 : speed;
          setAnimationDuration(adaptiveSpeed);
        }
      }
    };

    // Check after initial render and whenever text changes
    checkOverflow();

    // Also check after images and fonts have loaded
    window.addEventListener("load", checkOverflow);

    // Check on window resize to handle responsive layouts
    window.addEventListener("resize", checkOverflow);

    return () => {
      window.removeEventListener("load", checkOverflow);
      window.removeEventListener("resize", checkOverflow);
    };
  }, [text, speed]);

  const animateStyle: React.CSSProperties = {
    ...styles.animate,
    animation: `scrollText ${animationDuration}s linear infinite`,
  };

  return (
    <div ref={containerRef} style={styles.scrollingContainer} className="scrolling-text-container">
      <div
        ref={textRef}
        className={`${className} ${shouldScroll ? "scrolling" : ""}`}
        style={{
          ...styles.scrollingText,
          ...(shouldScroll ? animateStyle : {}),
        }}
        aria-label={text}
      >
        {shouldScroll
          ? text.split(separator).map((item, index, array) => (
              <React.Fragment key={index}>
                {item}
                {index < array.length - 1 && separator && (
                  <span className="text-warning px-4">{separator}</span>
                )}
              </React.Fragment>
            ))
          : text}
      </div>
    </div>
  );
};

export default ScrollingText;
